<template>
	<div class="calculator__configurator">
		<h5 class="calculator__configurator-title">{{ $t('defaultDistribution') }}</h5>

		<div class="calculator__configurator-columns">
			<div class="calculator__configurator-selection">
				<p
					v-if="$t('defaultDistributionHint')"
					class="calculator__configurator-text"
					v-html="$t('defaultDistributionHint')"
				></p>

				<ul class="calculator__configurator-items">
					<transition-group @enter="enter" @leave="leave">
						<li
							v-for="item in items"
							:key="item.key"
							class="calculator__configurator-item"
						>
							<fieldset>
								<legend>{{ item.title }}</legend>

								<p v-if="item.hint" class="calculator__configurator-hint">
									<span v-html="item.hint" />

									<button
										class="mdc-button calculator__configurator-hint-toggle"
										type="button"
										:aria-expanded="isHintExpanded(item.key)"
										@click="onHintToggle(item.key)"
									>
										<i
											class="material-icons mdc-button__icon"
											role="presentation"
											></i
										>
										<span class="ln-u-visuallyhidden">{{
											$t('showMore')
										}}</span>
									</button>
								</p>

								<transition @enter="enter" @leave="leave">
									<p
										v-if="isHintExpanded(item.key)"
										class="calculator__configurator-hint-extended"
										v-html="item.hintExtended"
									></p>
								</transition>

								<Radio
									:name="item.key"
									value="yes"
									:required="true"
									:checked="item.isChecked"
									:label="$t('yes')"
									@change="onToggle($event, item.key, true)"
								/>

								<Radio
									:name="item.key"
									value="no"
									:required="true"
									:checked="item.isChecked === false"
									:label="$t('no')"
									@change="onToggle($event, item.key, false)"
								/>

								<InputHint
									:name="item.key"
									class="calculator__configurator-item-hint"
								/>

								<transition @enter="enter" @leave="leave">
									<ul
										v-if="item.isChecked && item.parts"
										class="calculator__configurator-item-parts"
									>
										<transition-group @enter="enter" @leave="leave">
											<li v-for="(part, index) in item.parts" :key="part.key">
												<ConfiguratorItem
													:item="part"
													:parent="item"
													:groupKey="item.key"
													:level="1"
													:index="index"
												/>
											</li>
										</transition-group>
									</ul>
								</transition>

								<AddButton
									v-if="item.isChecked && item.showAdd"
									:addKey="item.key"
									:groupKey="item.key"
									:level="1"
								/>
							</fieldset>
						</li>
					</transition-group>
				</ul>
			</div>

			<div class="calculator__configurator-graphs">
				<Graph
					valueKey="statutoryValue"
					:shares="$store.getters.statutoryShares"
					:title="$t('statutoryShares')"
					class="calculator__configurator-graphs-item"
				/>
				<Graph
					valueKey="compulsoryValue"
					:shares="$store.getters.compulsoryShares"
					:title="$t('compulsoryShares')"
					class="calculator__configurator-graphs-item"
				/>
			</div>
		</div>

		<Navigation :continueLabel="continueLabel" :onContinue="onContinue" />
	</div>
</template>

<script>
import Graph from './Graph.vue';
import ConfiguratorItem from './ConfiguratorItem.vue';
import Navigation from './Navigation.vue';
import Radio from './Radio.vue';
import InputHint from './InputHint.vue';
import AddButton from './AddButton.vue';
import animate from '../utils/animate';

export default {
	name: 'Configurator',
	mixins: [animate],
	components: {
		Graph,
		ConfiguratorItem,
		Navigation,
		Radio,
		InputHint,
		AddButton
	},
	data() {
		return {
			hintExpanded: {}
		};
	},
	computed: {
		items() {
			const revertedLastIndex = this.$store.getters.configuratorItems
				.slice(0)
				.reverse()
				.findIndex(item => item.isChecked !== undefined);
			const index =
				revertedLastIndex > -1
					? this.$store.getters.configuratorItems.length - revertedLastIndex
					: 0;
			const items = this.$store.getters.configuratorItems.slice(0, index + 1);

			return items;
		},
		continueLabel() {
			const continueLabel =
				this.$store.state.customNavigation.continueLabel || this.$t(`goToCustomizer`);

			return continueLabel;
		}
	},
	mounted() {
		this.$el.addEventListener('keydown', event => {
			if (event.which === 13) {
				event.preventDefault();

				this.onContinue();
			}
		});
	},
	methods: {
		onToggle(event, key, isChecked) {
			if (event.target.checked) {
				this.$store.commit('toggleConfiguratorItem', {
					key,
					isChecked
				});
			}
		},
		async onContinue() {
			const form = this.$el.closest('form');

			if (form && form.Bouncer) {
				const invalid = await form.Bouncer.validateAll(this.$el);

				if (invalid.length) {
					console.log('Validation error', invalid);

					return;
				}
			}

			this.$store.commit('showCustomizer');
		},
		onHintToggle(key) {
			this.hintExpanded[key] = !this.hintExpanded[key];
		},
		isHintExpanded(key) {
			return this.hintExpanded[key];
		}
	}
};
</script>

<style>
@import url('../assets/css/variables.css');

.calculator__configurator-title {
	font-size: 35px;
	font-weight: 200;
	margin: 0 0 26px 0;
}

.calculator__configurator-text.calculator__configurator-text {
	margin: 0 0 48px 0;
}

.calculator__configurator-hint.calculator__configurator-hint {
	margin: 0 0 16px 0;
}

.calculator__configurator-items {
	list-style-type: none;
	padding: 0;
	margin: 0 0 26px 0;
}

.calculator__configurator-item {
	margin-top: 24px;
	border-top: 1px solid rgba(0, 0, 0, 0.1);
	padding-top: 24px;
}

.calculator__configurator-items fieldset {
	border: 0;
	margin: 0;
	padding: 0;
}
.calculator__configurator-items legend {
	margin: 0 0 12px 0;
	padding: 0;
	font-weight: bold;
}

.calculator__configurator-item-parts {
	list-style-type: none;
	padding: 0;
	margin-top: 24px;
	margin-bottom: 12px;
}

.calculator__configurator-hint-extended.calculator__configurator-hint-extended {
	padding-bottom: 20px;
	margin-left: var(--spacing);
}
.calculator__configurator-hint-extended-content {
	margin-top: 8px;
}
.calculator__configurator-hint-extended-content p:first-child {
	margin-top: 0;
}

.calculator__configurator-graphs {
	margin-top: 48px;
	background: var(--color-background-dark);
	padding: 1.5rem;
}

.calculator__configurator-graphs-item:not(:first-child) {
	margin-top: 48px;
}

/* Overwrites */
.calculator__configurator-item-hint.mdc-text-field-helper-line {
	padding-left: 0.75rem;
}

.calculator__configurator-hint-toggle.mdc-button {
	min-width: 0;
	min-height: 0;
	margin-top: -4px;
}
.calculator__configurator-hint-toggle .mdc-button__icon {
	margin-right: 0;
}

/* Tablet */
@media (min-width: 48rem) {
	.calculator__configurator-graphs {
		margin-top: 0;
		position: sticky;
		top: 0;
		align-self: flex-start;
	}
}

/* Desktop */
@media (min-width: 64rem) {
	.calculator__configurator-columns {
		display: flex;
		margin-left: calc(-1 * var(--spacing));
		margin-right: calc(-1 * var(--spacing));
	}

	.calculator__configurator-selection {
		flex: 0 0 66.667%;
		padding: 0 var(--spacing);
		box-sizing: border-box;
	}

	.calculator__configurator-graphs {
		flex-basis: 33.333%;
		box-sizing: border-box;
		min-width: 0;
		margin-top: 0;
		margin-right: var(--spacing);
	}
	.calculator__configurator-graphs-item {
		margin-top: 0;
	}
}
</style>
